<template>
  <b-card
    v-if="account && !hideDismissibleNotice && !lifecycle.hidden"
    :border-variant="lifecycle.variant"
    class="account-onboarding-status"
    :data-lifecycle-state="lifecycle.state"
  >
    <div class="d-flex align-items-start">
      <feather-icon
        :icon="lifecycle.icon"
        size="24"
        :class="`text-${lifecycle.variant} mr-1 mt-25`"
      />
      <div class="flex-grow-1">
        <div class="d-flex align-items-center mb-50">
          <span
            v-if="lifecycle.decoration === 'bouquet'"
            aria-hidden="true"
            class="dashboard-welcome-bouquet mr-50"
            data-testid="dashboard-welcome-bouquet"
          >💐</span>
          <h3 class="mb-0">
            {{ $t(lifecycle.titleKey) }}
          </h3>
        </div>

        <p
          v-for="bodyKey in lifecycle.bodyKeys"
          :key="bodyKey"
          class="mb-50"
        >
          {{ $t(bodyKey, { count: lifecycle.daysLeft }) }}
        </p>

        <!-- Body copy interpolating the SUBSCRIPTION access-end date, which is
             a different date from the approval review deadline below. -->
        <p
          v-if="accessEndText"
          class="mb-50"
          data-testid="subscription-access-end-copy"
        >
          {{ accessEndText }}
          <time
            class="sr-only"
            :datetime="lifecycle.bodyDate"
          >{{ formattedAccessEnd }}</time>
        </p>

        <p
          v-if="deadlineText"
          class="mb-50 font-weight-bold"
        >
          {{ deadlineText }}
          <time
            class="sr-only"
            :datetime="lifecycle.review.deadlineAt"
          >{{ formattedDeadline }}</time>
        </p>

        <p
          v-if="lifecycle.showChannel"
          class="mb-50"
        >
          {{ $t(lifecycle.emailVerified
            ? 'approvalLifecycle.channel.verified'
            : 'approvalLifecycle.channel.unverified') }}
        </p>

        <div
          v-if="lifecycle.showReason"
          class="review-note mb-1"
          data-testid="approval-review-note"
        >
          <strong>{{ $t('approvalLifecycle.rejected.reasonLabel') }}</strong>
          <span>{{ lifecycle.reviewComment || $t('approvalLifecycle.rejected.missingReason') }}</span>
        </div>

        <p
          v-if="lifecycle.promptKey"
          class="mb-1"
        >
          {{ $t(lifecycle.promptKey) }}
        </p>

        <p
          v-if="lifecycle.showPromotion && lifecycle.promotionRedeemed"
          class="mb-1"
          data-testid="promotion-redeemed-copy"
        >
          {{ $t('approvalLifecycle.rejected.promotionUsed') }}
        </p>

        <b-button
          v-if="showPrimaryAction"
          :variant="lifecycle.variant === 'warning' ? 'primary' : lifecycle.variant"
          class="mt-50"
          :to="{ name: lifecycle.action.route }"
        >
          {{ $t(lifecycle.action.key) }}
        </b-button>

        <template v-if="showApprovedActions">
          <b-button
            variant="success"
            class="mr-1 mt-50"
            :to="{ name: 'clientlist' }"
          >
            {{ $t('Add My First Client') }}
          </b-button>
          <!-- This optional shortcut stays hidden while the Guide remains
               available through its sidebar entry and route. -->
          <b-button
            v-if="guideEnabled"
            variant="outline-primary"
            class="mt-50"
            :to="{ name: 'guide' }"
          >
            {{ $t('Watch Quick Tour') }}
          </b-button>
        </template>

        <b-button
          v-if="showDismissAction"
          variant="link"
          class="dismiss-notice-button mt-50"
          data-testid="dismiss-approval-notice"
          @click="dismissNotice"
        >
          {{ $t('approvalLifecycle.approved.dismiss') }}
        </b-button>
      </div>
    </div>
  </b-card>
</template>

<script>
import { BButton, BCard } from 'bootstrap-vue'
import {
  formatApprovalLifecycleDate,
  resolveApprovalLifecycle,
} from '@/libs/approvalLifecycle'
import { GUIDE_ENABLED } from '@/libs/featureFlags'

const APPROVED_NOTICE_DISMISSAL_PREFIX = 'approval-notice-dismissed'
// PM 14.08.2026 item 3. Keyed by user AND subscription term: a genuinely new
// subscription after a lapse is a new term and gets its own greeting.
const SUBSCRIPTION_WELCOME_PREFIX = 'subscription-welcome-shown'

export default {
  components: { BButton, BCard },
  props: {
    account: {
      type: Object,
      required: true,
    },
    hideActions: {
      type: Boolean,
      default: false,
    },
    surface: {
      type: String,
      default: 'default',
      validator: value => ['default', 'account-settings', 'dashboard'].includes(value),
    },
    dismissible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dismissed: false,
      subscriptionWelcomeSeen: false,
      guideEnabled: GUIDE_ENABLED,
    }
  },
  computed: {
    lifecycle() {
      return resolveApprovalLifecycle(this.account, {
        surface: this.surface,
        subscriptionWelcomeSeen: this.subscriptionWelcomeSeen,
      })
    },
    // Empty when the surface is wrong or either half of the key is missing.
    // An empty key means "cannot track", which must SHOW the welcome rather
    // than suppress it -- a user who is never greeted is worse than one
    // greeted twice.
    subscriptionWelcomeKey() {
      if (this.surface !== 'dashboard' || !this.account) return ''
      const userKey = Reflect.get(this.account, '_id') || this.account.id
      const startedAt = this.lifecycle.subscriptionStartedAt
      if (!userKey || !startedAt) return ''
      return `${SUBSCRIPTION_WELCOME_PREFIX}:${userKey}:${startedAt}`
    },
    dismissalKey() {
      if (!this.dismissible || !this.account) return ''
      const approval = this.account.approval || {}
      const userKey = Reflect.get(this.account, '_id') || this.account.id
      const decisionKey = Reflect.get(approval, '_id')
        || approval.reviewedAt
        || approval.submittedAt
        || approval.status
      if (!userKey || !decisionKey) return ''
      return `${APPROVED_NOTICE_DISMISSAL_PREFIX}:${userKey}:${decisionKey}`
    },
    hideDismissibleNotice() {
      if (!this.dismissible) return false
      if (['approved-access', 'approved-free-access', 'approved-subscription-recovery'].includes(this.lifecycle.state)) return true
      return this.lifecycle.state === 'approved-no-access' && this.dismissed
    },
    showDismissAction() {
      return this.dismissible && this.lifecycle.state === 'approved-no-access'
    },
    formattedDeadline() {
      return formatApprovalLifecycleDate(
        this.lifecycle.review.deadlineAt,
        (this.$i18n && this.$i18n.locale) || 'tr-TR',
      )
    },
    deadlineText() {
      if (!this.lifecycle.deadlineKey || !this.formattedDeadline) return ''
      return this.$t(this.lifecycle.deadlineKey, { date: this.formattedDeadline })
    },
    formattedAccessEnd() {
      return formatApprovalLifecycleDate(
        this.lifecycle.bodyDate,
        (this.$i18n && this.$i18n.locale) || 'tr-TR',
      )
    },
    accessEndText() {
      if (!this.lifecycle.bodyDateKey || !this.formattedAccessEnd) return ''
      return this.$t(this.lifecycle.bodyDateKey, { date: this.formattedAccessEnd })
    },
    onSettingsPage() {
      return this.surface === 'account-settings'
    },
    showPrimaryAction() {
      if (this.hideActions || !this.lifecycle.action) return false
      return !(this.onSettingsPage && this.lifecycle.action.route === 'account-settings')
    },
    showApprovedActions() {
      return !this.hideActions
        && ['approved-access', 'approved-free-access'].includes(this.lifecycle.state)
        && !this.account.hasClients
    },
  },
  watch: {
    dismissalKey: {
      immediate: true,
      handler() {
        this.loadDismissal()
      },
    },
    subscriptionWelcomeKey: {
      immediate: true,
      handler() {
        this.loadSubscriptionWelcome()
      },
    },
  },
  mounted() {
    this.markSubscriptionWelcomeShown()
  },
  updated() {
    this.markSubscriptionWelcomeShown()
  },
  methods: {
    loadSubscriptionWelcome() {
      if (!this.subscriptionWelcomeKey || typeof localStorage === 'undefined') {
        this.subscriptionWelcomeSeen = false
        return
      }
      try {
        this.subscriptionWelcomeSeen = localStorage.getItem(this.subscriptionWelcomeKey) === '1'
      } catch {
        this.subscriptionWelcomeSeen = false
      }
    },
    // Written when the welcome is ON SCREEN, not when it is dismissed: the user
    // may never navigate away cleanly, and "once" has to survive that. The
    // in-memory flag is deliberately NOT flipped here -- that would make the
    // card vanish under the user mid-visit. It suppresses the NEXT mount.
    markSubscriptionWelcomeShown() {
      if (this.lifecycle.state !== 'subscription-welcome') return
      if (!this.subscriptionWelcomeKey || typeof localStorage === 'undefined') return
      try {
        localStorage.setItem(this.subscriptionWelcomeKey, '1')
      } catch {
        // Storage unavailable: the welcome shows again next time, which is a
        // better failure than throwing on the dashboard.
      }
    },
    loadDismissal() {
      if (!this.dismissalKey || typeof localStorage === 'undefined') {
        this.dismissed = false
        return
      }
      try {
        this.dismissed = localStorage.getItem(this.dismissalKey) === '1'
      } catch {
        this.dismissed = false
      }
    },
    dismissNotice() {
      this.dismissed = true
      if (!this.dismissalKey || typeof localStorage === 'undefined') return
      try {
        localStorage.setItem(this.dismissalKey, '1')
      } catch {
        // Local dismissal still works when storage is unavailable.
      }
    },
  },
}
</script>

<style scoped>
.account-onboarding-status {
  border-width: 1px;
}

.dashboard-welcome-bouquet {
  font-size: 1.75rem;
  line-height: 1;
}

.review-note {
  display: flex;
  flex-direction: column;
  padding: 0.875rem 1rem;
  border-radius: 0.5rem;
  background: rgba(255, 159, 67, 0.1);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.dismiss-notice-button {
  padding-left: 0;
  padding-right: 0;
}
</style>
